import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useWallet } from '@/hooks/useWallet';
import { useAuth } from '@/hooks/useAuth';
import { CollectionToken } from '@/lib/types';
import { fetchOwnedCollectionNFTs, fetchNFTDetails } from '@/lib/tzkt';
import { getCollectionsByNetwork, getCollectionByAddress, CollectionInfo } from '@/lib/collections';
import { getAnimatedThumbnailUrl } from '@/lib/ipfs';
import { getUserFavorites } from '@/lib/favorites';
import { HeartButton } from '@/components/ui/HeartButton';
import { useFavorites } from '@/hooks/useFavorites';

interface GroupedNFTs {
  collection: CollectionInfo;
  tokens: CollectionToken[];
  visibleCount: number; // Track how many to show per collection
}

interface ListingNFT extends CollectionToken {
  collection: CollectionInfo;
}

interface FavoritedNFT {
  contractAddress: string;
  tokenId: string;
  favoritedAt: string;
  favoritesCount: number;
  metadata?: {
    name: string;
    image: string;
  };
  collection?: CollectionInfo;
}

const INITIAL_VISIBLE = 12;
const LOAD_MORE_COUNT = 12;

// Lazy-loading image component using Intersection Observer
function LazyImage({
  src,
  alt,
  className,
  onError
}: {
  src: string;
  alt: string;
  className?: string;
  onError?: () => void;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' } // Start loading 100px before entering viewport
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className="w-full h-full bg-gray-200 dark:bg-gray-700">
      {isInView && (
        <img
          src={src}
          alt={alt}
          className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
          onLoad={() => setIsLoaded(true)}
          onError={() => onError?.()}
        />
      )}
      {(!isInView || !isLoaded) && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
}

function NFTCard({
  token,
  collection,
  onListClick
}: {
  token: CollectionToken;
  collection: CollectionInfo;
  onListClick: (nft: ListingNFT) => void;
}) {
  const [hasError, setHasError] = useState(false);

  // Use animated WebP thumbnails - preserves animation while being 50-80% smaller
  const thumbnailUrl = token.metadata.image
    ? getAnimatedThumbnailUrl(token.metadata.image, 256)
    : null;
  const fallbackUrl = `https://ui-avatars.com/api/?name=${token.tokenId}&size=300&background=random`;

  const handleListClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onListClick({ ...token, collection });
  };

  return (
    <Link
      to={`/nft/${token.tokenAddress}/${token.tokenId}`}
      className="group block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow hover:shadow-lg transition-shadow"
    >
      <div className="aspect-square bg-gray-200 dark:bg-gray-700 overflow-hidden relative">
        {thumbnailUrl && !hasError ? (
          <LazyImage
            src={thumbnailUrl}
            alt={token.metadata.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setHasError(true)}
          />
        ) : (
          <img
            src={fallbackUrl}
            alt={token.metadata.name}
            className="w-full h-full object-cover"
          />
        )}
        {/* List for Sale button overlay */}
        <button
          onClick={handleListClick}
          className="absolute bottom-2 right-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg
            opacity-0 group-hover:opacity-100 transition-opacity hover:bg-blue-700 active:scale-95"
        >
          List for Sale
        </button>
      </div>
      <div className="p-3">
        <h3 className="font-medium text-gray-900 dark:text-white truncate">{token.metadata.name}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">#{token.tokenId}</p>
      </div>
    </Link>
  );
}

// Listing Modal Component
function ListingModal({
  nft,
  onClose,
  onSuccess,
  tezos,
  address,
}: {
  nft: ListingNFT;
  onClose: () => void;
  onSuccess: () => void;
  tezos: any;
  address: string;
}) {
  const [price, setPrice] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isListing, setIsListing] = useState(false);
  const [step, setStep] = useState<'approve' | 'list'>('approve');

  const handleApprove = async () => {
    const marketplaceContract = import.meta.env.VITE_MARKETPLACE_CONTRACT;
    if (!marketplaceContract) {
      alert('Marketplace contract not configured');
      return;
    }

    setIsApproving(true);

    try {
      const nftContract = await tezos.contract.at(nft.tokenAddress);

      // FA2 update_operators entrypoint
      const op = await nftContract.methods.update_operators([
        {
          add_operator: {
            owner: address,
            operator: marketplaceContract,
            token_id: nft.tokenId,
          },
        },
      ]).send();

      await op.confirmation();
      setStep('list');
    } catch (error) {
      console.error('Failed to approve:', error);
      alert('Failed to approve. Please try again.');
    } finally {
      setIsApproving(false);
    }
  };

  const handleList = async () => {
    if (!price) return;

    const marketplaceContract = import.meta.env.VITE_MARKETPLACE_CONTRACT;
    if (!marketplaceContract) {
      alert('Marketplace contract not configured');
      return;
    }

    setIsListing(true);

    try {
      const contract = await tezos.contract.at(marketplaceContract);

      const op = await contract.methods.list_nft(
        nft.tokenAddress,
        nft.tokenId,
        Math.floor(parseFloat(price) * 1000000) // Convert to mutez
      ).send();

      await op.confirmation();

      alert('NFT listed successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to list NFT:', error);
      alert('Failed to list NFT. Make sure you have approved the marketplace first.');
    } finally {
      setIsListing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* NFT Preview */}
        <div className="flex gap-4 mb-6">
          <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-700 flex-shrink-0">
            <img
              src={getAnimatedThumbnailUrl(nft.metadata.image, 128)}
              alt={nft.metadata.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <img
                src={nft.collection.thumbnail}
                alt={nft.collection.name}
                className="w-5 h-5 rounded-full"
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">{nft.collection.name}</span>
            </div>
            <h3 className="font-bold text-gray-900 dark:text-white truncate">{nft.metadata.name}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">#{nft.tokenId}</p>
          </div>
        </div>

        {/* Price Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Listing Price
          </label>
          <div className="relative">
            <input
              type="number"
              step="0.01"
              min="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
              className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg
                focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg
                bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
              ꜩ
            </span>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-3">
          <button
            onClick={handleApprove}
            disabled={isApproving || step === 'list'}
            className={`w-full px-6 py-3 rounded-lg font-medium transition-all ${
              step === 'list'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-300 dark:border-green-700'
                : 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50'
            }`}
          >
            {step === 'list' ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Approved
              </span>
            ) : isApproving ? (
              'Check wallet to approve...'
            ) : (
              '1. Approve Marketplace'
            )}
          </button>
          <button
            onClick={handleList}
            disabled={isListing || !price || step === 'approve'}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
          >
            {isListing ? 'Check wallet to confirm...' : '2. List for Sale'}
          </button>
        </div>

        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          First approve the marketplace to transfer your NFT, then list it for sale.
        </p>
      </div>
    </div>
  );
}

export default function MyNFTsPage() {
  const { isConnected, address, tezos, network, wallet } = useWallet();
  const { isAuthenticated } = useAuth(wallet, address);
  const { toggleFavorite, isLoading: isFavoriteLoading, updateStatus } = useFavorites();
  const [groupedNFTs, setGroupedNFTs] = useState<GroupedNFTs[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [listingNFT, setListingNFT] = useState<ListingNFT | null>(null);

  // Favorites state
  const [favoritedNFTs, setFavoritedNFTs] = useState<FavoritedNFT[]>([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoritesVisibleCount, setFavoritesVisibleCount] = useState(INITIAL_VISIBLE);

  // Map Beacon network type to our network type
  const getNetworkType = (beaconNetwork: string | null): 'mainnet' | 'ghostnet' | 'sandbox' => {
    switch (beaconNetwork) {
      case 'mainnet':
        return 'mainnet';
      case 'ghostnet':
        return 'ghostnet';
      case 'custom':
      case 'sandbox':
        return 'sandbox';
      default:
        return 'sandbox'; // Default to sandbox for unknown networks
    }
  };

  useEffect(() => {
    if (isConnected && address) {
      fetchUserNFTs();
    } else {
      setLoading(false);
    }
  }, [isConnected, address, network]);

  // Fetch user favorites when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchUserFavorites();
    } else {
      setFavoritedNFTs([]);
    }
  }, [isAuthenticated]);

  const fetchUserFavorites = async () => {
    setFavoritesLoading(true);
    try {
      const response = await getUserFavorites({ limit: 100 });

      // Fetch metadata for each favorited NFT
      const nftsWithMetadata = await Promise.all(
        response.items.map(async (fav) => {
          const collection = getCollectionByAddress(fav.contractAddress);
          try {
            const nftDetails = await fetchNFTDetails(fav.contractAddress, fav.tokenId, 'mainnet');
            return {
              ...fav,
              metadata: nftDetails ? {
                name: nftDetails.metadata.name,
                image: nftDetails.metadata.image,
              } : undefined,
              collection: collection || undefined,
            };
          } catch {
            return {
              ...fav,
              collection: collection || undefined,
            };
          }
        })
      );

      setFavoritedNFTs(nftsWithMetadata);

      // Update favorite statuses in the hook
      for (const nft of nftsWithMetadata) {
        updateStatus(nft.contractAddress, nft.tokenId, {
          isFavorited: true,
          favoritesCount: nft.favoritesCount,
        });
      }
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    } finally {
      setFavoritesLoading(false);
    }
  };

  const handleUnfavorite = async (contractAddress: string, tokenId: string) => {
    try {
      await toggleFavorite(contractAddress, tokenId, true, () => {});
      // Remove from local state
      setFavoritedNFTs(prev => prev.filter(
        nft => !(nft.contractAddress === contractAddress && nft.tokenId === tokenId)
      ));
    } catch (error) {
      console.error('Failed to unfavorite:', error);
    }
  };

  const fetchUserNFTs = async () => {
    if (!address) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const networkType = getNetworkType(network);
      const collections = getCollectionsByNetwork(networkType);
      console.log(`[MyNFTs] Network: ${network} -> ${networkType}, Collections:`, collections.map(c => c.name));
      const groups: GroupedNFTs[] = [];
      let total = 0;

      // Fetch owned NFTs for each collection in parallel
      const results = await Promise.all(
        collections.map(async (collection) => {
          const tokens = await fetchOwnedCollectionNFTs(collection.address, address);
          return { collection, tokens };
        })
      );

      // Filter collections that have NFTs owned by user
      for (const result of results) {
        if (result.tokens.length > 0) {
          groups.push({
            ...result,
            visibleCount: INITIAL_VISIBLE, // Start with limited visibility
          });
          total += result.tokens.length;
        }
      }

      setGroupedNFTs(groups);
      setTotalCount(total);
    } catch (error) {
      console.error('Failed to fetch user NFTs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = useCallback((collectionSlug: string) => {
    setGroupedNFTs(prev =>
      prev.map(group =>
        group.collection.slug === collectionSlug
          ? { ...group, visibleCount: group.visibleCount + LOAD_MORE_COUNT }
          : group
      )
    );
  }, []);

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-md mx-auto text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">My Collection</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Connect your wallet to view your PFP collection
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              We'll show your NFTs from collections on your connected network
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Collection</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {loading ? 'Loading...' : `${totalCount} NFT${totalCount !== 1 ? 's' : ''} across ${groupedNFTs.length} collection${groupedNFTs.length !== 1 ? 's' : ''}`}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading your NFTs...</p>
          </div>
        ) : groupedNFTs.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Items Found</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You don't own any NFTs from our featured collections yet
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
            >
              Browse Collections
            </Link>
          </div>
        ) : (
          <div className="space-y-12">
            {groupedNFTs.map(({ collection, tokens, visibleCount }) => {
              const visibleTokens = tokens.slice(0, visibleCount);
              const hasMore = tokens.length > visibleCount;
              const remaining = tokens.length - visibleCount;

              return (
                <div key={collection.slug}>
                  {/* Collection Header */}
                  <div className="flex items-center justify-between mb-4">
                    <Link
                      to={`/collection/${collection.slug}`}
                      className="flex items-center gap-3 group"
                    >
                      <img
                        src={collection.thumbnail}
                        alt={collection.name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {collection.name}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {tokens.length} item{tokens.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </Link>
                    <Link
                      to={`/collection/${collection.slug}`}
                      className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                    >
                      View Collection →
                    </Link>
                  </div>

                  {/* NFT Grid - Only show visible tokens */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {visibleTokens.map((token) => (
                      <NFTCard
                        key={`${token.tokenAddress}-${token.tokenId}`}
                        token={token}
                        collection={collection}
                        onListClick={setListingNFT}
                      />
                    ))}
                  </div>

                  {/* Load More Button */}
                  {hasMore && (
                    <div className="mt-6 text-center">
                      <button
                        onClick={() => handleLoadMore(collection.slug)}
                        className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 font-medium transition-colors"
                      >
                        Show More ({remaining} remaining)
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Favorited NFTs Section */}
        {isAuthenticated && (
          <div className="mt-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Favorited
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {favoritesLoading ? 'Loading...' : `${favoritedNFTs.length} item${favoritedNFTs.length !== 1 ? 's' : ''}`}
                </p>
              </div>
            </div>

            {favoritesLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-red-500"></div>
                <p className="mt-2 text-gray-600 dark:text-gray-400 text-sm">Loading favorites...</p>
              </div>
            ) : favoritedNFTs.length === 0 ? (
              <div className="text-center py-12 bg-gray-100 dark:bg-gray-800 rounded-xl">
                <svg
                  className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
                <p className="text-gray-600 dark:text-gray-400">No favorites yet</p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                  Click the heart icon on any NFT to add it to your favorites
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {favoritedNFTs.slice(0, favoritesVisibleCount).map((nft) => {
                    const thumbnailUrl = nft.metadata?.image
                      ? getAnimatedThumbnailUrl(nft.metadata.image, 256)
                      : null;
                    const fallbackUrl = `https://ui-avatars.com/api/?name=${nft.tokenId}&size=300&background=random`;

                    return (
                      <div
                        key={`${nft.contractAddress}-${nft.tokenId}`}
                        className="group relative bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow hover:shadow-lg transition-shadow"
                      >
                        <Link to={`/nft/${nft.contractAddress}/${nft.tokenId}`}>
                          <div className="aspect-square bg-gray-200 dark:bg-gray-700 overflow-hidden relative">
                            {thumbnailUrl ? (
                              <LazyImage
                                src={thumbnailUrl}
                                alt={nft.metadata?.name || `Token #${nft.tokenId}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                onError={() => {}}
                              />
                            ) : (
                              <img
                                src={fallbackUrl}
                                alt={nft.metadata?.name || `Token #${nft.tokenId}`}
                                className="w-full h-full object-cover"
                              />
                            )}
                          </div>
                          <div className="p-3">
                            <h3 className="font-medium text-gray-900 dark:text-white truncate">
                              {nft.metadata?.name || `Token #${nft.tokenId}`}
                            </h3>
                            {nft.collection && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {nft.collection.name}
                              </p>
                            )}
                          </div>
                        </Link>
                        {/* Unfavorite button */}
                        <div className="absolute top-2 right-2">
                          <HeartButton
                            isFavorited={true}
                            favoritesCount={nft.favoritesCount}
                            isLoading={isFavoriteLoading(nft.contractAddress, nft.tokenId)}
                            onClick={() => handleUnfavorite(nft.contractAddress, nft.tokenId)}
                            size="sm"
                            showCount={false}
                            className="bg-white/90 dark:bg-gray-800/90 shadow-sm"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Load More Favorites */}
                {favoritedNFTs.length > favoritesVisibleCount && (
                  <div className="mt-6 text-center">
                    <button
                      onClick={() => setFavoritesVisibleCount(prev => prev + LOAD_MORE_COUNT)}
                      className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 font-medium transition-colors"
                    >
                      Show More ({favoritedNFTs.length - favoritesVisibleCount} remaining)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Listing Modal */}
      {listingNFT && address && tezos && (
        <ListingModal
          nft={listingNFT}
          onClose={() => setListingNFT(null)}
          onSuccess={fetchUserNFTs}
          tezos={tezos}
          address={address}
        />
      )}
    </div>
  );
}
